/**
 * Inventory panel — opens with I, closes with I or Escape (Issue #13).
 *
 * Shows a list of items with name + count.  Updates incrementally as the
 * local player's inventory changes.
 */

export interface InventoryItem {
  itemId: string;
  count: number;
}

const ITEM_LABELS: Record<string, string> = {
  gold_coin: 'Gold Coin',
};

export interface InventoryPanelHandle {
  isOpen(): boolean;
  toggle(): void;
  open(): void;
  close(): void;
  setItems(items: Map<string, number>): void;
  dispose(): void;
}

export function createInventoryPanel(): InventoryPanelHandle {
  let open = false;

  const root = document.createElement('div');
  root.id = 'inventory';
  Object.assign(root.style, {
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    minWidth: '320px',
    background: 'rgba(0,0,0,0.85)',
    border: '2px solid #c0a060',
    padding: '20px 24px',
    fontFamily: '"Courier New", monospace',
    color: '#c0c0c0',
    zIndex: '50',
    display: 'none',
    boxShadow: '0 0 40px rgba(192,160,96,0.3)',
  } as CSSStyleDeclaration);

  const title = document.createElement('h2');
  title.textContent = 'Inventory';
  Object.assign(title.style, {
    margin: '0 0 16px 0',
    color: '#c0a060',
    fontSize: '20px',
    letterSpacing: '4px',
    textAlign: 'center',
  } as CSSStyleDeclaration);

  const list = document.createElement('div');
  list.id = 'inventory-list';
  Object.assign(list.style, {
    minHeight: '120px',
    minWidth: '260px',
  } as CSSStyleDeclaration);

  const hint = document.createElement('p');
  hint.textContent = 'Press I or Esc to close';
  Object.assign(hint.style, {
    margin: '16px 0 0 0',
    color: '#5a4a30',
    fontSize: '11px',
    textAlign: 'center',
  } as CSSStyleDeclaration);

  root.appendChild(title);
  root.appendChild(list);
  root.appendChild(hint);
  document.body.appendChild(root);

  function render(items: Map<string, number>): void {
    list.innerHTML = '';
    if (items.size === 0) {
      const empty = document.createElement('div');
      empty.textContent = '(empty)';
      empty.style.color = '#5a4a30';
      empty.style.fontStyle = 'italic';
      list.appendChild(empty);
      return;
    }
    const rows = Array.from(items.entries()).sort(([a], [b]) => a.localeCompare(b));
    for (const [itemId, count] of rows) {
      const row = document.createElement('div');
      Object.assign(row.style, {
        display: 'flex',
        justifyContent: 'space-between',
        padding: '4px 0',
        borderBottom: '1px dotted #3a2a14',
      } as CSSStyleDeclaration);
      const name = document.createElement('span');
      name.textContent = ITEM_LABELS[itemId] ?? itemId;
      const qty = document.createElement('span');
      qty.textContent = `×${count}`;
      qty.style.color = '#c0a060';
      row.appendChild(name);
      row.appendChild(qty);
      list.appendChild(row);
    }
  }

  return {
    isOpen: () => open,
    toggle() {
      if (open) this.close();
      else this.open();
    },
    open() {
      if (open) return;
      open = true;
      root.style.display = 'block';
    },
    close() {
      if (!open) return;
      open = false;
      root.style.display = 'none';
    },
    setItems(items) {
      render(items);
    },
    dispose() {
      root.remove();
    },
  };
}
